import classNames from "classnames";
import { useMemo, useContext } from "react";
import { useFormContext } from "react-hook-form";
import type { ZypeSubscriptionPlan, PWSSessionBackend } from "@inferno/renderer-shared-core";
import { DATETIME_DISPLAY_FORMATS, zonedTimeFormat, type Store } from "@inferno/renderer-shared-core";
import { MobXProviderContext } from "mobx-react";
import { Button, ButtonKind, MagicLink } from "../../../../../core/ui";
import Heading from "../../../../../core/ui/Heading.component";
import Spinner from "../../../../../core/ui/Spinner.component";
import { CheckBoxInput, FORM_ERRORS } from "../../forms";
import { CoastSubscriptionDisclaimer } from "../common/CoastSubscriptionDisclaimer.component";
import "./CoastSignUpReviewAndSubmit.style.scss";

interface CoastSignUpReviewAndSubmitProps {
  plans: ZypeSubscriptionPlan[];
  isProcessing: boolean;
  subscribe: () => void;
  onCancel: () => void;
  goBack: () => void;
  stepNumber: number;
  stepCount: number;
  hidden: boolean;
  isGift: boolean;
  planId: string;
  session?: PWSSessionBackend;
}

export interface CoastSignUpReviewAndSubmitInputs {
  acceptAutoRenewal: boolean;
  acceptTerms: boolean;
}

export function CoastSignUpReviewAndSubmit({
  isProcessing,
  stepNumber,
  stepCount,
  subscribe,
  onCancel,
  goBack,
  plans,
  isGift,
  hidden,
  planId,
  session,
}: CoastSignUpReviewAndSubmitProps) {
  const { register, handleSubmit, errors } = useFormContext<CoastSignUpReviewAndSubmitInputs>();
  const { store } = useContext(MobXProviderContext) as { store?: Store };

  const selectedPlan = useMemo(() => plans.find(p => p._id === planId), [planId, plans]);

  const country = (store?.request?.countryCode || "").toUpperCase();

  const isSupportedCountry = country === "CA" || country === "US";

  const createdAt = session?.state?.profile?.created_at;
  const timezone = store?.site?.index?.timeZone || store?.env?.DEFAULT_TIMEZONE || "UTC";
  const effectiveDate = zonedTimeFormat({
    date: createdAt || new Date(),
    timezone,
    outputFormat: DATETIME_DISPLAY_FORMATS.FULL_MONTH_SHORT_DAY_FULL_YEAR,
  });

  if (hidden) {
    return null;
  }

  if (!selectedPlan) {
    // we should never get here
    return (
      <section className="no-plan-selected">
        <p className="step-indicator">
          Step {stepNumber} of {stepCount}
        </p>
        <Heading level={2} className="section-title">
          Review and Submit
        </Heading>
        <p className="section-info">Seems like you haven&apos;t picked a plan</p>
        <p className="section-info">Please go back and select a plan that you like</p>
        <div className="button-prev-next">
          <button className="button-previous" type="button" onClick={goBack}>
            Previous
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className={classNames("component-coast-sign-up-review-and-submit", "sign-up-step", { hidden })}>
      <p className="step-indicator">
        Step {stepNumber} of {stepCount}
      </p>

      <Heading level={2} className="section-title">
        Review and Submit
      </Heading>

      <div className="selected-plan">
        <span>
          {selectedPlan.name} {isGift ? "" : "Auto Renewal (US Dollars)"}
        </span>
        <span>${selectedPlan.amount}</span>
      </div>

      <hr className="divider" />

      <div className="total-billed-wrapper">
        <span className="total-billed">Total Billed (US Dollars)</span>
        <div className="total-amount">
          <span>${selectedPlan.amount}</span>
          <span className="sales-tax">+ sales tax where applicable</span>
        </div>
      </div>

      <hr className="divider" />

      {isSupportedCountry && (
        <>
          <div className="canadian-disclosure">
            <div className="disclosure-item">
              <span className="disclosure-label">Effective Date:</span>
              <span className="disclosure-value">{effectiveDate}</span>
            </div>
            <div className="disclosure-item">
              <span className="disclosure-label">Provider:</span>
              <span className="disclosure-value">
                Premiere Networks, Inc.
                <br />
                15260 Ventura Blvd, Sherman Oaks, CA 91403, USA
              </span>
            </div>
            <div className="disclosure-item">
              <span className="disclosure-label">Content Delivery:</span>
              <span className="disclosure-value">
                Content is delivered digitally via{" "}
                <a href="http://coasttocoastam.com/" target="_blank" rel="noopener noreferrer">
                  coasttocoastam.com
                </a>{" "}
                by logging in to your account.
                <br />
                <br />
                <span className="">This service is for US and Canadian residents only.</span>
              </span>
            </div>
          </div>
          <hr className="divider" />
        </>
      )}

      <CoastSubscriptionDisclaimer isGift={isGift} plan={selectedPlan} />

      <form onSubmit={handleSubmit(subscribe)}>
        {!isGift ? (
          <CheckBoxInput
            rules={{ required: FORM_ERRORS.required }}
            error={errors.acceptAutoRenewal}
            label="I accept auto-renewal"
            name="acceptAutoRenewal"
            register={register}
          />
        ) : null}

        <CheckBoxInput
          rules={{ required: FORM_ERRORS.required }}
          error={errors.acceptTerms}
          name="acceptTerms"
          register={register}
          label={
            <span>
              I accept &nbsp;
              <MagicLink external to="/subscriptions/" target="_blank" rel="noopener noreferrer">
                Coast to Coast Insider Subscription Terms
              </MagicLink>
              &nbsp;&amp;&nbsp;
              <MagicLink external to="/privacy/" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </MagicLink>
            </span>
          }
        />

        <div className="button-prev-next">
          <Button
            disabled={!!errors.acceptAutoRenewal || !!errors.acceptTerms || isProcessing}
            kind={ButtonKind.CUSTOMIZED}
            className="button-continue"
            type="submit"
          >
            {isProcessing ? <Spinner visible={true} /> : "Subscribe"}
          </Button>
          <Button kind={ButtonKind.CUSTOMIZED} className="button-previous" disabled={isProcessing} click={goBack}>
            Previous
          </Button>
        </div>
      </form>

      <Button kind={ButtonKind.CUSTOMIZED} className="button-cancel" disabled={isProcessing} click={onCancel}>
        Cancel
      </Button>
    </section>
  );
}
